import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth-context';
import { overtimeAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Moon, Clock, CheckCircle2, XCircle, Timer, Hourglass, Plus, Trash2, ListPlus, Info } from 'lucide-react';

function fmtDuration(sec) {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return `${h > 0 ? h + 'h ' : ''}${m}m${h === 0 ? ' ' + s + 's' : ''}`;
}

const STATUS_BADGE = {
  running: { label: 'Running', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  pending: { label: 'Pending approval', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700 border-red-200' },
};

function StatusBadge({ status }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return <Badge variant="outline" className={s.cls}>{s.label}</Badge>;
}

// ---------- Staff: manual after-hours logging ----------
function TimerPanel() {
  const [loading, setLoading] = useState(true);
  const [mine, setMine] = useState([]);
  const blankEntry = () => ({ date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '', file_ref: '', description: '' });
  const [manual, setManual] = useState([blankEntry()]);
  const [submitting, setSubmitting] = useState(false);
  const updateEntry = (i, f, v) => setManual(m => m.map((e, idx) => idx === i ? { ...e, [f]: v } : e));
  const addEntry = () => setManual(m => [...m, blankEntry()]);
  const removeEntry = (i) => setManual(m => m.length === 1 ? m : m.filter((_, idx) => idx !== i));

  const load = useCallback(async () => {
    try {
      const m = await overtimeAPI.mine();
      setMine(m.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitManual = async () => {
    for (const e of manual) {
      if (!e.date || !e.start_time || !e.end_time || !e.file_ref.trim()) {
        toast.error('Fill date, start, end and file for every entry'); return;
      }
      if (e.description.trim().length < 10) {
        toast.error('Add a detailed description (min 10 characters) for every entry'); return;
      }
    }
    setSubmitting(true);
    try {
      const res = await overtimeAPI.manual(manual);
      const n = res.data.created;
      toast.success(`${n} ${n === 1 ? 'entry' : 'entries'} submitted for approval`);
      setManual([blankEntry()]);
      load();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to submit entries');
    } finally { setSubmitting(false); }
  };

  if (loading) return <p className="text-muted-foreground py-10 text-center">Loading…</p>;

  return (
    <div className="space-y-6">
      {/* Approval disclaimer */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex gap-2" data-testid="overtime-disclaimer">
        <Info size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          Extra time is <strong>not counted automatically</strong>. Each entry you submit is reviewed by management and is only added to your after-hours total <strong>once approved</strong>.
        </p>
      </div>

      {/* Manual entry */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2"><ListPlus size={18} className="text-primary" /> Log time manually</CardTitle>
          <CardDescription>Already worked the extra time? Add it here. You can add multiple entries — each needs a detailed note of what you worked on.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {manual.map((e, i) => (
            <div key={i} className="rounded-lg border p-3 space-y-3" data-testid={`manual-entry-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Entry {i + 1}</span>
                {manual.length > 1 && (
                  <Button variant="ghost" size="sm" className="text-destructive h-7 px-2" onClick={() => removeEntry(i)} data-testid={`manual-remove-${i}`}>
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="space-y-1"><Label className="text-xs">Date</Label><Input type="date" value={e.date} onChange={ev => updateEntry(i, 'date', ev.target.value)} data-testid={`manual-date-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">Start</Label><Input type="time" value={e.start_time} onChange={ev => updateEntry(i, 'start_time', ev.target.value)} data-testid={`manual-start-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">End</Label><Input type="time" value={e.end_time} onChange={ev => updateEntry(i, 'end_time', ev.target.value)} data-testid={`manual-end-${i}`} /></div>
                <div className="space-y-1"><Label className="text-xs">File no. / name</Label><Input value={e.file_ref} onChange={ev => updateEntry(i, 'file_ref', ev.target.value)} placeholder="FILE-2026-0473" data-testid={`manual-file-${i}`} /></div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">What did you work on? (detailed) <span className="text-destructive">*</span></Label>
                <Textarea value={e.description} onChange={ev => updateEntry(i, 'description', ev.target.value)} placeholder="Describe the work done in detail…" rows={2} data-testid={`manual-desc-${i}`} />
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={addEntry} className="gap-2" data-testid="manual-add-entry"><Plus size={15} /> Add another entry</Button>
            <Button onClick={submitManual} disabled={submitting} className="gap-2" data-testid="manual-submit"><ListPlus size={15} /> {submitting ? 'Submitting…' : 'Submit entries'}</Button>
          </div>
        </CardContent>
      </Card>

      {/* My history */}
      <Card>
        <CardHeader><CardTitle className="text-lg font-heading">My after-hours log</CardTitle></CardHeader>
        <CardContent className="p-0">
          {mine.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No after-hours time logged yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>File</TableHead><TableHead>Started</TableHead><TableHead>Duration</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {mine.map(s => (
                    <TableRow key={s.id} data-testid={`my-session-${s.id}`}>
                      <TableCell className="font-medium">{s.file_ref}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{s.local_started_at || '—'}</TableCell>
                      <TableCell className="tabular-nums">{fmtDuration(s.duration_seconds)}</TableCell>
                      <TableCell><StatusBadge status={s.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Manager: approvals ----------
function ApprovalsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await overtimeAPI.list('pending'); setRows(r.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, approve) => {
    try {
      if (approve) await overtimeAPI.approve(id);
      else await overtimeAPI.reject(id, '');
      toast.success(approve ? 'Approved' : 'Rejected');
      load();
    } catch (e) { console.error(e); toast.error('Action failed'); }
  };

  if (loading) return <p className="text-muted-foreground py-10 text-center">Loading…</p>;
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg font-heading">Pending approvals</CardTitle></CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="text-center text-muted-foreground py-8 text-sm">Nothing awaiting approval.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Staff</TableHead><TableHead>File</TableHead><TableHead>Started</TableHead><TableHead>Duration</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map(s => (
                  <TableRow key={s.id} data-testid={`approval-row-${s.id}`}>
                    <TableCell className="font-medium">{s.user_name}</TableCell>
                    <TableCell>{s.file_ref}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.local_started_at}</TableCell>
                    <TableCell className="tabular-nums">{fmtDuration(s.duration_seconds)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{s.note || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => act(s.id, true)} data-testid={`approve-${s.id}`}><CheckCircle2 size={16} /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(s.id, false)} data-testid={`reject-${s.id}`}><XCircle size={16} /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Manager: report ----------
function ReportPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    overtimeAPI.report().then(r => setData(r.data)).catch(e => console.error(e)).finally(() => setLoading(false));
  }, []);
  if (loading) return <p className="text-muted-foreground py-10 text-center">Loading…</p>;
  if (!data) return null;
  return (
    <div className="space-y-6">
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Approved total</p><p className="text-2xl font-heading font-semibold tabular-nums">{fmtDuration(data.total_seconds)}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Awaiting approval</p><p className="text-2xl font-heading font-semibold tabular-nums">{data.pending_count}</p></CardContent></Card>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-lg font-heading">By staff</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.by_staff.length === 0 ? <p className="text-center text-muted-foreground py-8 text-sm">No approved time yet.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Sessions</TableHead><TableHead className="text-right">Time</TableHead></TableRow></TableHeader>
                <TableBody>{data.by_staff.map(r => (
                  <TableRow key={r.user_id}><TableCell className="font-medium">{r.user_name}</TableCell><TableCell>{r.sessions}</TableCell><TableCell className="text-right tabular-nums">{fmtDuration(r.total_seconds)}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-lg font-heading">By file</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.by_file.length === 0 ? <p className="text-center text-muted-foreground py-8 text-sm">No approved time yet.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>File</TableHead><TableHead>Sessions</TableHead><TableHead className="text-right">Time</TableHead></TableRow></TableHeader>
                <TableBody>{data.by_file.map((r, i) => (
                  <TableRow key={r.file_ref + i}><TableCell className="font-medium">{r.file_ref}</TableCell><TableCell>{r.sessions}</TableCell><TableCell className="text-right tabular-nums">{fmtDuration(r.total_seconds)}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function OvertimePage() {
  const { isSuperUser, isManager } = useAuth();
  const isAdmin = isSuperUser || isManager;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="overtime-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight flex items-center gap-2">
          <Moon size={22} className="text-primary" /> After-Hours Time
        </h1>
        <p className="text-muted-foreground">Track extra time worked from home, tagged to a file</p>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="timer">
          <TabsList data-testid="overtime-tabs">
            <TabsTrigger value="timer" data-testid="tab-timer"><Timer size={14} className="mr-1.5" /> My Time</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="tab-approvals"><Hourglass size={14} className="mr-1.5" /> Approvals</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report"><Clock size={14} className="mr-1.5" /> Report</TabsTrigger>
          </TabsList>
          <TabsContent value="timer" className="mt-4"><TimerPanel /></TabsContent>
          <TabsContent value="approvals" className="mt-4"><ApprovalsPanel /></TabsContent>
          <TabsContent value="report" className="mt-4"><ReportPanel /></TabsContent>
        </Tabs>
      ) : (
        <TimerPanel />
      )}
    </div>
  );
}
